/**
 * 顶栏模块上次停留的 Tab。
 * 只放内存：SPA 进详情/换模块后点顶栏可回到离开前的 Tab；
 * 浏览器大刷、关标签页、登出后回到模块默认入口。
 *
 * 项目排期不走这里：顶栏始终进列表 /schedule，详情是钻取不是模块 Tab。
 */

export type TabbedModuleId = 'dashboard' | 'config' | 'calendar-config';

type TabbedModuleSpec = {
  prefix: string;
  defaultPath: string;
  tabs: readonly string[];
};

const TABBED_MODULES: Record<TabbedModuleId, TabbedModuleSpec> = {
  dashboard: {
    prefix: '/dashboard',
    defaultPath: '/dashboard/overview',
    tabs: ['overview', 'report', 'monitor'],
  },
  config: {
    prefix: '/config',
    defaultPath: '/config/templates',
    tabs: ['templates', 'nodes', 'autoCompleteRules'],
  },
  'calendar-config': {
    prefix: '/calendar-config',
    defaultPath: '/calendar-config/settings',
    tabs: ['settings', 'permission', 'monitor-node-config'],
  },
};

const DEFAULT_PATH_TO_MODULE: Record<string, TabbedModuleId> = {
  '/dashboard/overview': 'dashboard',
  '/config/templates': 'config',
  '/calendar-config/settings': 'calendar-config',
};

const lastPaths = new Map<TabbedModuleId, string>();

function tabFromPath(pathname: string, prefix: string): string | null {
  if (pathname !== prefix && !pathname.startsWith(`${prefix}/`)) return null;
  const tab = pathname.slice(prefix.length).replace(/^\//, '');
  return tab || null;
}

/** 访问合法模块 Tab 时写入；非法路径不覆盖上次记录。 */
export function rememberModulePath(pathname: string): void {
  for (const [id, spec] of Object.entries(TABBED_MODULES) as Array<[TabbedModuleId, TabbedModuleSpec]>) {
    const tab = tabFromPath(pathname, spec.prefix);
    if (!tab || !spec.tabs.includes(tab)) continue;
    lastPaths.set(id, `${spec.prefix}/${tab}`);
    return;
  }
}

export function getModuleLastPath(moduleId: TabbedModuleId): string {
  return lastPaths.get(moduleId) ?? TABBED_MODULES[moduleId].defaultPath;
}

/** 顶栏菜单 key 是模块默认入口；有上次 Tab 则回到上次 Tab。 */
export function resolveMenuNavigatePath(menuKey: string): string {
  const moduleId = DEFAULT_PATH_TO_MODULE[menuKey];
  if (!moduleId) return menuKey;
  return getModuleLastPath(moduleId);
}

export function clearAllModuleLastPaths(): void {
  lastPaths.clear();
}
